// getPropertyRows.tsx (use .tsx because it returns JSX)

import React from 'react'
import {
  ArchitectureIcon,
  BathRoomIcon,
  BedIcon,
  BuiltIcon,
  ConditionIcon,
  HeatingIcon,
  ParkingIcon,
  PoolIcon,
  SquareFootageIcon,
  StoriesIcon,
} from './Icons'

import { getGridSizes, MeasureVariant } from './functions/functions'
import { Building } from '@sales-center/common'

export type PropertyRow = {
  icon: React.ReactNode
  label: string
  gridSize: Record<string, string | number>
  width?: number
  height?: number
}

export const getPropertyRows = (
  building: Building | undefined | null,
  variant: MeasureVariant = 'detail',
  size: 'medium' | 'large' = 'medium',
  iconColor?: string,
): PropertyRow[] => {
  const sizeMap = {
    medium: { width: 24, height: 24 },
    large: { width: 35, height: 35 },
  }

  const { width, height } = sizeMap[size] || sizeMap.medium

  return [
    {
      icon: <BedIcon width={width} height={height} fill={iconColor} />,
      label: building?.bedroomsCount
        ? `${building.bedroomsCount} Bed${building.bedroomsCount === 1 ? '' : 's'}`
        : 'N/A',
      gridSize: getGridSizes(variant, 8, 12),
    },
    {
      icon: <BathRoomIcon width={width} height={height} fill={iconColor} />,
      label: building?.bathroomsCount
        ? `${building.bathroomsCount} Bathroom${building.bathroomsCount === 1 ? '' : 's'}`
        : 'N/A',
      gridSize: getGridSizes(variant, 4, 12),
    },
    {
      icon: <StoriesIcon width={width} height={height} fill={iconColor} />,
      label: building?.storiesCount
        ? `${+building.storiesCount} ${+building.storiesCount === 1 ? 'Story' : 'Stories'}`
        : 'N/A',
      gridSize: getGridSizes(variant, 8, 12),
    },
    {
      icon: <BuiltIcon width={width} height={height} fill={iconColor} />,
      label: building?.yearBuilt ? `Built in ${building.yearBuilt}` : 'N/A',
      gridSize: getGridSizes(variant, 4, 12),
    },
    {
      icon: <HeatingIcon width={width} height={height} fill={iconColor} />,
      label: building?.heatingTypeCode ?? 'No heating Type',
      gridSize: getGridSizes(variant, 8, 12),
    },
    {
      icon: <ConditionIcon width={width} height={height} fill={iconColor} />,
      label: building?.conditionCode
        ? `Condition: "${building.conditionCode}"`
        : 'Condition: N/A',
      gridSize: getGridSizes(variant, 4, 12),
    },
    {
      icon: <ArchitectureIcon width={width} height={height} fill={iconColor} />,
      label: building?.architectureTypeCode
        ? `Architecture: "${building.architectureTypeCode}"`
        : 'Architecture: N/A',
      gridSize: getGridSizes(variant, 8, 12),
    },
    {
      icon: <PoolIcon width={width} height={height} fill={iconColor} />,
      label: building?.pool?.typeCode ?? 'None',
      gridSize: getGridSizes(variant, 4, 12),
    },
    {
      icon: <ParkingIcon width={width} height={height} fill={iconColor} />,
      label: building?.parking?.typeCode ?? 'N/A',
      gridSize: getGridSizes(variant, 8, 12),
    },
    {
      icon: (
        <SquareFootageIcon width={width} height={height} fill={iconColor} />
      ),
      label: building?.buildingAreaSqFt
        ? `${building.buildingAreaSqFt.toLocaleString()} Sq. Ft.`
        : 'N/A',
      gridSize: getGridSizes(variant, 4, 12),
    },
  ]
}
